import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input, InputSignal, viewChild } from '@angular/core';
import { FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { RegisterUserFormService } from 'src/app/pages/register-user/services/register-user-form.service';
import { BaseFormField } from 'src/app/pages/register-user/shared/form/abstracts';
import { DateRange, DefaultMatCalendarRangeStrategy, MatCalendar, MatDatepickerModule, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { GoogleTranslateService } from 'src/app/services/google-translate.service';
import { LocaleCode } from './interfaces/calendar-register-user.interface';
import dayjs from 'dayjs';
import { TranslationService } from 'src/app/services/translation.service';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { BreakpointsService } from 'src/app/services/breakpoints.service';

@Component({
  selector: 'app-date-field',
  standalone: true,
  imports: [
    TranslateModule, 
    MatFormFieldModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatDatepickerModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: DateFieldComponent
    }
  ],
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFieldComponent extends BaseFormField<Date> {
  public calendarRef = viewChild<MatCalendar<Date>>('calendarRef');  
  
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlNameStartDate: InputSignal<string> = input.required<string>();
  public formControlNameEndDate: InputSignal<string> = input.required<string>();
  public isNextButtonClicked: InputSignal<boolean> = input<boolean>();
  
  private _datePipe = inject(DatePipe);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _selectionModel = inject(MatRangeDateSelectionModel<Date>)
  private _selectionStrategy = inject(DefaultMatCalendarRangeStrategy<Date>);
  private _googleTranslateService = inject(GoogleTranslateService);
  private _translationService = inject(TranslationService);
  private _breakpointsService = inject(BreakpointsService);
  private _adapter: DateAdapter<Date> = inject(DateAdapter);
  private _cdr = inject(ChangeDetectorRef); 

  private _startDateControl = computed(() => this.form().get(this.formControlNameStartDate()));
  private _endDateControl = computed(() => this.form().get(this.formControlNameEndDate()));
  private _selectedLanguage = this._translationService.selectedLanguage;
  private _localeCode = computed(() => this._googleTranslateService.cookieLangMapping(this._selectedLanguage()));
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _isMobile = this._breakpointsService.isMobile;
  
  public minDate: Date = new Date();
  public selectedDateRange: DateRange<Date> | undefined;
  
  markAllFieldsAsTouchedForDesktop = effect(() => {
    if (!this._isMobile() && this._markAllFormFieldsAsTouched()) {
      this._startDateControl()?.markAsTouched();
      this._startDateControl()?.markAsDirty();
      this._endDateControl()?.markAsTouched();
      this._endDateControl()?.markAsDirty();
      
      // Triggear change detection manually for OnPush
      this._cdr.detectChanges();
    }
  }, { allowSignalWrites: true });
  
  loadCalendarLanguage = effect(
    () => {

      if (this._localeCode() === LocaleCode['EN_GB']) {
        this._adapter.getFirstDayOfWeek = () => 0; // set first day of week to Sunday
      } else {
        this._adapter.getFirstDayOfWeek = () => 1; // set first day of week to Monday
      }

      this._adapter.setLocale(this._localeCode()); // update language of calendar according to selected language
    },
    { allowSignalWrites: true }
  );
  
  public rangeChanged(selectedDate: Date) {
    const selection = this._selectionModel.selection;
    const newSelection = this._selectionStrategy.selectionFinished(selectedDate, selection);

    this._selectionModel.updateSelection(newSelection, this);
    this.selectedDateRange = new DateRange<Date>(newSelection.start, newSelection.end);

    this._startDateControl()?.markAsTouched();
    this._startDateControl()?.markAsDirty();
    this._endDateControl()?.markAsTouched();
    this._endDateControl()?.markAsDirty();

    if (this.selectedDateRange.start) {
      const formattedStartDate = this._datePipe.transform(this.selectedDateRange.start, 'dd/MM/yyyy');
      this._startDateControl()?.setValue(formattedStartDate);
      this._registerUserFormService.setStartDate(this.selectedDateRange.start);
    } else {
      this._startDateControl()?.setValue('');
      this._registerUserFormService.setStartDate(new Date());
    }
    
    if (this.selectedDateRange.end) {
      const formattedEndDate = this._datePipe.transform(this.selectedDateRange.end, 'dd/MM/yyyy');
      this._endDateControl()?.setValue(formattedEndDate);
      this._registerUserFormService.setEndDate(this.selectedDateRange.end);

      if (this._isMobile()) {
        closeAllCiSnackbar(); // close snackbars for mobile UX
      }
    } else {
      this._endDateControl()?.setValue(null);
      this._registerUserFormService.setEndDate(null);
    }
  }
  
  public getDate(type: string): string {
    let result = '-';
    
    if (this.selectedDateRange?.[type]) {
      const date = this.selectedDateRange[type];
      dayjs.locale(this._selectedLanguage());
      return dayjs(date).format('ddd D MMM');
    }

    return result;
  }
  
  public isInvalidField(): boolean {
    if (this._isMobile()) {
      // mobile: check only when next button is clicked
      if (this.isNextButtonClicked()) {
        return (this._startDateControl()?.hasError('required') && (this._startDateControl()?.touched || this._startDateControl()?.dirty)) ||
               (this._endDateControl()?.hasError('required') && (this._endDateControl()?.touched || this._endDateControl()?.dirty));
      } else {
        return false;
      }
    } else {
    
    return (this._startDateControl()?.hasError('required') && (this._startDateControl()?.touched || this._startDateControl()?.dirty)) ||
           (this._endDateControl()?.hasError('required') && (this._endDateControl()?.touched || this._endDateControl()?.dirty));
    }
  }
}
